import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  ArrowLeft,
  UserPlus,
  UserMinus,
  Trash2,
  BellRing,
  ExternalLink,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  Users,
  Building2,
  Mail
} from "lucide-react";
import { toast } from "sonner";
import { toSydneyDate, formatTimestamp } from "../utils/helpers";

interface AssignmentUser {
  id: string;
  name: string;
  email: string;
  department: string;
  jobRole: string;
  assignedDate: string;
  readDate?: string;
  signedDate?: string;
  status: 'Assigned' | 'Read' | 'Signed' | 'Overdue';
}

interface AssignmentDetails {
  id: string; // Assignment ID
  documentId: string; // Policy/Memo ID
  name: string;
  type: 'Policy' | 'Memo';
  version?: number;
  number?: string;
  requireSignature: boolean;
  effectiveDate: string;
  documentStatus: string;
  assignmentPriority: 'Low' | 'Med' | 'High' | 'Critical';
  overdueDays?: number;
  createdDate: string;
  dueDate?: string;
  createdBy: {
    name: string;
    email: string;
  };
  users: AssignmentUser[];
  stats: {
    totalAssigned: number;
    totalRead: number;
    totalSigned: number;
    completionPercentage: number;
  };
  recentActivity: {
    timestamp: string;
    userId: string;
    userName: string;
    action: string;
    description: string;
  }[];
}

export default function MemoAssignPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { getDepartmentName } = useData();

  const [assignment, setAssignment] = useState<AssignmentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [isSendingReminders, setIsSendingReminders] = useState(false);

  // Calculate overdue days - returns undefined if not overdue, number of days if overdue
  const calculateOverdueDays = (dueDate: string): number | undefined => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = now.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : undefined;
  };

  // Calculate due date based on priority and creation date
  // This implements business logic for assignment deadlines
  const calculateDueDate = (createdDate: string, priority: 'Low' | 'Med' | 'High' | 'Critical'): string => {
    // Business rules for assignment deadlines based on priority
    const daysToAdd = {
      'Critical': 3,  // 3 days - urgent compliance/safety items
      'High': 7,      // 1 week - important policies  
      'Med': 14,      // 2 weeks - standard policies
      'Low': 30       // 1 month - general information
    };

    const created = new Date(createdDate);
    const due = new Date(created);
    due.setDate(due.getDate() + daysToAdd[priority]);

    // Set to end of day (23:59:59) for clear deadline
    due.setHours(23, 59, 59, 999);

    return due.toISOString();
  };

  // Generate realistic assignment creation date (simulate recent assignment)
  const generateRecentAssignmentDate = (): string => {
    const now = new Date();
    // Random assignment created between 1-30 days ago
    const daysAgo = Math.floor(Math.random() * 30) + 1;
    const createdDate = new Date(now);
    createdDate.setDate(createdDate.getDate() - daysAgo);

    // Set to business hours (9 AM - 5 PM)
    const hour = Math.floor(Math.random() * 8) + 9; // 9-17
    const minute = Math.floor(Math.random() * 60);
    createdDate.setHours(hour, minute, 0, 0);

    return createdDate.toISOString();
  };

  const memoId = searchParams.get('memoId');

  // Load assignment details
  useEffect(() => {
    const loadAssignmentDetails = async () => {
      if (!memoId) return;

      setIsLoading(true);
      try {
        // This would fetch assignment details for the specific memo
        // For now, using mock data structure with realistic date logic
        const assignmentPriority: 'Low' | 'Med' | 'High' | 'Critical' = 'Med';
        const createdDate = generateRecentAssignmentDate();
        const dueDate = calculateDueDate(createdDate, assignmentPriority);

        const mockAssignment: AssignmentDetails = {
          id: `assignment-${memoId}`, // Unique assignment ID
          documentId: memoId, // The actual memo ID
          name: 'Important Company Update',
          type: 'Memo',
          number: 'MEMO-2024-001',
          requireSignature: false,
          effectiveDate: '2024-01-15T00:00:00Z',
          documentStatus: 'Active',
          assignmentPriority: assignmentPriority,
          createdDate: createdDate,
          dueDate: dueDate,
          createdBy: {
            name: 'Jane Director',
            email: 'jane.director@company.com'
          },
          users: [
            {
              id: '1',
              name: 'Alice Johnson',
              email: 'alice@company.com',
              department: '300',
              jobRole: 'Operations Manager',
              assignedDate: createdDate, // Same as assignment creation
              readDate: (() => {
                const readDate = new Date(createdDate);
                readDate.setHours(14, 30, 0, 0);
                return readDate.toISOString();
              })(),
              status: 'Read'
            },
            {
              id: '2',
              name: 'Bob Smith',
              email: 'bob@company.com',
              department: '300',
              jobRole: 'Operations Assistant',
              assignedDate: createdDate, // Same as assignment creation
              status: 'Assigned'
            }
          ],
          stats: {
            totalAssigned: 25,
            totalRead: 18,
            totalSigned: 0,
            completionPercentage: 72
          },
          recentActivity: [
            {
              timestamp: createdDate,
              userId: 'admin1',
              userName: 'Jane Director',
              action: 'created',
              description: 'Assignment created with 25 users'
            },
            {
              timestamp: (() => {
                const readDate = new Date(createdDate);
                readDate.setHours(14, 30, 0, 0);
                return readDate.toISOString();
              })(),
              userId: '1',
              userName: 'Alice Johnson',
              action: 'read',
              description: 'Read the memo'
            }
          ]
        };

        // Calculate overdue days if there's a due date and it's actually overdue
        if (mockAssignment.dueDate) {
          const overdueDays = calculateOverdueDays(mockAssignment.dueDate);
          if (overdueDays !== undefined) {
            mockAssignment.overdueDays = overdueDays;
          }
        }

        setAssignment(mockAssignment);
      } catch (error) {
        console.error('Error loading assignment details:', error);
        toast.error('Failed to load assignment details');
      } finally {
        setIsLoading(false);
      }
    };

    loadAssignmentDetails();
  }, [memoId]);

  // Derived: users eligible for reminders (not Signed or Read)
  const eligibleUsers = assignment?.users.filter(u => u.status !== 'Signed' && u.status !== 'Read') || [];
  const allEligibleSelected = eligibleUsers.length > 0 && eligibleUsers.every(u => selectedUserIds.has(u.id));

  const toggleSelectAll = () => {
    if (allEligibleSelected) {
      setSelectedUserIds(prev => {
        const next = new Set(prev);
        eligibleUsers.forEach(u => next.delete(u.id));
        return next;
      });
    } else {
      setSelectedUserIds(prev => {
        const next = new Set(prev);
        eligibleUsers.forEach(u => next.add(u.id));
        return next;
      });
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSendReminders = async () => {
    if (!assignment || selectedUserIds.size === 0) return;
    setIsSendingReminders(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/memos/${assignment.documentId}/send-reminders`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ userIds: Array.from(selectedUserIds) })
        }
      );
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(data.message || `Reminders sent to ${data.sent} user(s)`);
        setSelectedUserIds(new Set());
      } else {
        toast.error(data.message || 'Failed to send reminders');
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setIsSendingReminders(false);
    }
  };

  const getPriorityBadge = (priority: string) => {
    const variants = {
      'Low': 'bg-blue-100 text-blue-800',
      'Med': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-orange-100 text-orange-800',
      'Critical': 'bg-red-100 text-red-800'
    } as const;

    return (
      <Badge className={variants[priority as keyof typeof variants] || variants.Med}>
        {priority}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      'Assigned': 'bg-blue-100 text-blue-800',
      'Read': 'bg-yellow-100 text-yellow-800',
      'Signed': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800'
    } as const;

    return (
      <Badge className={variants[status as keyof typeof variants] || variants.Assigned}>
        {status}
      </Badge>
    );
  };

  // if (!canManageMemos) {
  //   return (
  //     <div className="p-6">
  //       <Card>
  //         <CardContent className="p-6">
  //           <div className="text-center">
  //             <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
  //             <h3 className="mb-2">Access Restricted</h3>
  //             <p className="text-muted-foreground">
  //               You don't have permission to view assignment details. Contact your administrator if you need access.
  //             </p>
  //           </div>
  //         </CardContent>
  //       </Card>
  //     </div>
  //   );
  // }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <div className="h-64 bg-gray-200 rounded"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
            <div className="space-y-6">
              <div className="h-32 bg-gray-200 rounded"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="mb-2">Assignment Not Found</h3>
              <p className="text-muted-foreground">
                The assignment you're looking for doesn't exist or has been removed.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/policies?tab=assignments">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{assignment.name}</h1>
            <p className="text-muted-foreground">
              {assignment.number && `${assignment.number} • `}
              {toSydneyDate(assignment.effectiveDate)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <UserMinus className="h-4 w-4 mr-2" />
            Recall Assignment
          </Button>
          <Button variant="outline" size="sm">
            <UserPlus className="h-4 w-4 mr-2" />
            Add People
          </Button>
          <Button variant="outline" size="sm">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Assignment
          </Button>
          <Button variant="default" size="sm">
            <BellRing className="h-4 w-4 mr-2" />
            Send Reminders
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Side - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Document Section */}
          <Card>
            <CardHeader>
              <CardTitle>Document</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-blue-500" />
                <div>
                  <Link
                    to={`/memos/${assignment.documentId}`}
                    target="_blank"
                    className="text-lg font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    {assignment.name}
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                  <p className="text-sm text-muted-foreground">
                    {assignment.type} • {assignment.documentStatus}
                    {assignment.number && ` • ${assignment.number}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Read Tracking (for memos that don't require signatures) */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                {assignment.requireSignature ? 'Signature Tracking' : 'Read Tracking'}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                disabled={selectedUserIds.size === 0 || isSendingReminders}
                onClick={handleSendReminders}
              >
                {isSendingReminders ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Send Nudge
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Progress Overview */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progress</span>
                    <span>{assignment.stats.completionPercentage}% complete</span>
                  </div>
                  <Progress value={assignment.stats.completionPercentage} className="h-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    {assignment.requireSignature ? (
                      <>
                        <span>{assignment.stats.totalSigned} of {assignment.stats.totalAssigned} signed</span>
                        <span>{assignment.stats.totalRead} read</span>
                      </>
                    ) : (
                      <>
                        <span>{assignment.stats.totalRead} of {assignment.stats.totalAssigned} read</span>
                        <span>{assignment.stats.totalAssigned - assignment.stats.totalRead} pending</span>
                      </>
                    )}
                  </div>
                </div>

                {/* User List */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  <div className="flex items-center gap-2 pb-1 border-b">
                    <Checkbox
                      checked={allEligibleSelected}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all pending users"
                    />
                    <span className="text-sm font-medium text-muted-foreground">
                      Select all pending
                    </span>
                  </div>
                  {assignment.users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-2 border rounded">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedUserIds.has(user.id)}
                          disabled={user.status === 'Signed' || user.status === 'Read'}
                          onCheckedChange={() => toggleUserSelection(user.id)}
                          aria-label={`Select ${user.name}`}
                        />
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs">
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">{user.name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(user.status)}
                        {user.readDate && !user.signedDate && (
                          <span className="text-xs text-muted-foreground">
                            {toSydneyDate(user.readDate)}
                          </span>
                        )}
                        {user.signedDate && (
                          <span className="text-xs text-muted-foreground">
                            {toSydneyDate(user.signedDate)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}

                  {assignment.stats.totalAssigned > assignment.users.length && (
                    <div className="text-center py-2">
                      <Button variant="outline" size="sm">
                        View All {assignment.stats.totalAssigned} Users
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Side */}
        <div className="space-y-6">
          {/* Assignment Details */}
          <Card>
            <CardHeader>
              <CardTitle>Assignment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Priority</Label>
                <div className="mt-1">
                  {getPriorityBadge(assignment.assignmentPriority)}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Assignment Created On</Label>
                <div className="mt-1 text-sm">
                  {formatTimestamp(assignment.createdDate)}
                </div>
              </div>

              {assignment.dueDate && (
                <div>
                  <Label className="text-sm font-medium">Due Date</Label>
                  <div className="mt-1 text-sm">
                    {formatTimestamp(assignment.dueDate)}
                  </div>
                </div>
              )}

              {assignment.overdueDays && (
                <div>
                  <Label className="text-sm font-medium">Overdue by</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">{assignment.overdueDays} days</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Memo Details */}
          <Card>
            <CardHeader>
              <CardTitle>Memo Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Type</span>
                <Badge variant="outline">{assignment.type}</Badge>
              </div>

              {assignment.number && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Number</span>
                  <span className="text-sm">{assignment.number}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge variant="outline">{assignment.documentStatus}</Badge>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Signature Required</span>
                <span className="text-sm">
                  {assignment.requireSignature ? 'Yes' : 'No'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Effective Date</span>
                <span className="text-sm">{toSydneyDate(assignment.effectiveDate)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {assignment.recentActivity.map((activity, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1">
                      <p className="text-sm">
                        <span className="font-medium">{activity.userName}</span> {activity.description}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatTimestamp(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 